import React, { useState } from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import PreviewIcon from '@mui/icons-material/Visibility';
import { IconButton, Stack, Typography } from '@mui/material';

import { formatBytes } from '../../../features/file-upload';
import type { DocumentFromStorage } from '../DocumentManager.types.ts';

type ActionButtonsProps = {
  document: DocumentFromStorage | null;
  onPreview: (document: DocumentFromStorage) => void;
  isLoading: boolean;
  onDocumentDownload: undefined | ((documentId: string) => Promise<void>);
};

export const ActionButtons = ({ document, isLoading, onDocumentDownload, onPreview }: ActionButtonsProps) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleOnDocumentDownload = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (!onDocumentDownload || !document) {
      return;
    }

    setIsDownloading(true);

    onDocumentDownload(document.id).finally(() => {
      setIsDownloading(false);
    });
  };

  const handleOnPreviewDocument = (event: React.MouseEvent) => {
    if (!document) {
      return;
    }

    event.stopPropagation();
    onPreview(document);
  };

  return (
    <Stack direction="row" alignItems="center" gap={1} sx={{ '&:hover': { '& #file-size': { opacity: 0.7 } } }}>
      {!!document?.fileSize && (
        <Typography component="span" variant="caption" sx={{ opacity: 0 }} id="file-size">
          {formatBytes(document.fileSize)}
        </Typography>
      )}

      <IconButton
        edge="end"
        onClick={handleOnPreviewDocument}
        size="small"
        disabled={isLoading || !document}
        title={`Preview ${document?.fileName ?? 'the document'}`}
      >
        <PreviewIcon />
      </IconButton>

      {!!onDocumentDownload && (
        <IconButton
          edge="end"
          onClick={handleOnDocumentDownload}
          size="small"
          color="primary"
          disabled={isLoading || !document}
          loading={isDownloading}
          title="Download document"
        >
          <DownloadIcon />
        </IconButton>
      )}
    </Stack>
  );
};
